import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import api from '../../../utils/api'
import AdminLayout from '../components/AdminLayout';
import { AdminUser, AdminUpdateUserInput, REPORTING_CATEGORIES, TALENT_STATUSES, TALENT_SOURCES } from './types';
import { useUserTimezone } from '../../../hooks/useUserTimezone';

// Styled Components
const TabNav = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 24px;
`;

const TabLink = styled(Link)<{ $active?: boolean }>`
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ $active }) => ($active ? '#3b82f6' : '#6b7280')};
  text-decoration: none;
  border-bottom: 2px solid ${({ $active }) => ($active ? '#3b82f6' : 'transparent')};
  margin-bottom: -1px;
  transition: all 0.2s;

  &:hover {
    color: #3b82f6;
    background: #f9fafb;
  }
`;

const ContentLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 24px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const MainContent = styled.div``;

const Sidebar = styled.div``;

const FormSection = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 24px;
  margin-bottom: 24px;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 20px 0;
  padding-bottom: 12px;
  border-bottom: 1px solid #e5e7eb;
`;

const ActionsSection = styled(FormSection)`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div<{ $fullWidth?: boolean }>`
  grid-column: ${({ $fullWidth }) => ($fullWidth ? '1 / -1' : 'auto')};
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 6px;
`;

const ReadOnlyLabel = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  text-transform: uppercase;
  margin-bottom: 4px;
`;

const ReadOnlyValue = styled.p`
  font-size: 14px;
  color: #111827;
  margin: 0;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:disabled {
    background: #f3f4f6;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  min-height: 100px;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  margin-right: 8px;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
  padding: 8px 0;

  &:hover {
    color: #111827;
  }
`;

const CheckboxGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 4px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' | 'warning' }>`
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 8px;

  ${({ $variant = 'secondary' }) => {
    switch ($variant) {
      case 'primary':
        return 'background: #3b82f6; color: white; &:hover { background: #2563eb; }';
      case 'danger':
        return 'background: #ef4444; color: white; &:hover { background: #dc2626; }';
      case 'warning':
        return 'background: #f59e0b; color: white; &:hover { background: #d97706; }';
      default:
        return 'background: #e5e7eb; color: #374151; &:hover { background: #d1d5db; }';
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ActionLink = styled.a`
  padding: 10px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  background: #e5e7eb;
  color: #374151;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: background 0.2s;

  &:hover {
    background: #d1d5db;
  }
`;

const Toast = styled.div<{ $type: 'success' | 'error' }>`
  position: fixed;
  bottom: 24px;
  right: 24px;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  z-index: 1000;
  animation: slideIn 0.3s ease;

  ${({ $type }) =>
    $type === 'success' ? 'background: #10b981; color: white;' : 'background: #ef4444; color: white;'}

  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 48px;
  color: #6b7280;
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 48px;
  color: #ef4444;
`;

// Residence Table
const ResidenceTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 16px;

  th,
  td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid #e5e7eb;
    font-size: 14px;
  }

  th {
    font-weight: 600;
    color: #374151;
    background: #f9fafb;
  }

  td {
    color: #6b7280;
  }

  tr:last-child td {
    border-bottom: none;
  }
`;

const CurrentResidence = styled.h3`
  font-size: 16px;
  font-weight: 500;
  color: #111827;
  margin: 0 0 16px 0;
`;

function AdminUserEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { setTimezoneFromResponse, formatDateTime } = useUserTimezone();
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const [formData, setFormData] = useState<Partial<AdminUpdateUserInput>>({});
  const [user, setUser] = useState<AdminUser | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);

  // Determine active tab from URL
  const activeTab = location.pathname.includes('/publications')
    ? 'publications'
    : location.pathname.includes('/stories')
      ? 'stories'
      : location.pathname.includes('/audit_events')
        ? 'audit_events'
        : location.pathname.includes('/content_origin')
          ? 'content_origin'
          : 'info';

  const fetchUser = useCallback(async (userId: string) => {
    try {
      const response = await api.get(`/admin2/users/${userId}.json`);
      setTimezoneFromResponse(response.data);
      setUser(response.data.user);
    } catch (err: any) {
      setError(err.response?.data?.errors?.join(', ') || 'Failed to load user');
    } finally {
      setLoading(false);
    }
  }, [setTimezoneFromResponse]);

  useEffect(() => {
    if (id) {
      fetchUser(id);
    }
  }, [id, fetchUser]);

  useEffect(() => {
    if (user) {
      setFormData({
        id: user.id,
        email: user.email,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phoneNumber: user.phoneNumber || '',
        reportingCategory: user.reportingCategory || '',
        textOnlyEmail: user.textOnlyEmail || false,
        confirmedEmail: user.confirmed || false,
        talentManagerId: user.talentManagerId || '',
        talentStatus: user.talentStatus || '',
        talentSource: user.talentSource || '',
        talentTrained: user.talentTrained || false,
        availableFreelance: user.availableFreelance || false,
        internalNotes: user.internalNotes || '',
        expertiseIds: user.expertise?.map((e) => e.id) || [],
        adminManage: user.adminManage,
        adminImpersonate: user.adminImpersonate,
        adminManageCapabilities: user.adminManageCapabilities,
        adminCreateTransactions: user.adminCreateTransactions,
        adminEnabled: user.adminEnabled,
        adminManageFeatures: user.adminManageFeatures,
        adminManageUsers: user.adminManageUsers,
        adminBulkUploadUsers: user.adminBulkUploadUsers,
        adminManageAdminUser: user.adminManageAdminUser || false,
      });
    }
  }, [user]);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleInputChange = (field: keyof AdminUpdateUserInput, value: string | boolean | string[]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setUpdating(true);
    try {
      const payload = {
        user: {
          email: formData.email,
          first_name: formData.firstName,
          last_name: formData.lastName,
          phone_number: formData.phoneNumber,
          reporting_category: formData.reportingCategory,
          text_only_email: formData.textOnlyEmail,
          confirmed_email: formData.confirmedEmail ? '1' : '0',
          talent_manager_id: formData.talentManagerId || null,
          talent_status_id: formData.talentStatus || null,
          talent_source_id: formData.talentSource || null,
          talent_trained: formData.talentTrained,
          available_freelance: formData.availableFreelance,
          internal_notes: formData.internalNotes,
          expertise_ids: formData.expertiseIds || [],
          admin_manage: formData.adminManage,
          admin_impersonate: formData.adminImpersonate,
          admin_manage_capabilities: formData.adminManageCapabilities,
          admin_create_transactions: formData.adminCreateTransactions,
          admin_enabled: formData.adminEnabled,
          admin_manage_features: formData.adminManageFeatures,
          admin_manage_users: formData.adminManageUsers,
          admin_bulk_upload_users: formData.adminBulkUploadUsers,
          admin_manage_admin_user: formData.adminManageAdminUser,
        },
      };
      const response = await api.put(`/admin2/users/${id}.json`, payload);
      if (response.data?.success) {
        showToast('User updated successfully', 'success');
        // Refetch user to get updated data
        fetchUser(id!);
      } else {
        showToast(response.data?.errors?.join(', ') || 'Failed to update user', 'error');
      }
    } catch (err: any) {
      showToast(err.response?.data?.errors?.join(', ') || 'Failed to update user', 'error');
    }
    setUpdating(false);
  };

  const handleImpersonate = async () => {
    try {
      // Honor the server-computed destination. The legacy admin console did a
      // server redirect to `home_url`, which (for a now-signed-in user)
      // bounced through `redirect_if_signed_in` to `signed_in_root_path`
      // (= /tasks, or the user's portfolio/dashboard landing). The endpoint
      // returns that same path as `redirectUrl` (from `after_sign_in_path_for`).
      // Hardcoding '/' here instead landed the impersonator on the SPA Home
      // placeholder rather than Tasks — match legacy parity by following
      // `redirectUrl`, with '/tasks' as a defensive fallback.
      const { data } = await api.post(`/admin2/users/${id}/impersonate`);
      window.location.href = data?.redirectUrl || '/tasks';
    } catch (err: any) {
      showToast(err.response?.data?.errors?.join(', ') || 'Failed to impersonate user', 'error');
    }
  };

  const handleDisable = async () => {
    if (!window.confirm('Are you sure you want to disable this admin user?')) return;
    try {
      const response = await api.put(`/admin2/users/${id}/disable.json`);
      if (response.data?.success) {
        showToast('User disabled successfully', 'success');
        fetchUser(id!);
      } else {
        showToast(response.data?.errors?.join(', ') || 'Failed to disable user', 'error');
      }
    } catch (err: any) {
      showToast(err.response?.data?.errors?.join(', ') || 'Failed to disable user', 'error');
    }
  };

  const handleDelete = async () => {
    if (!user) return;
    const displayName = user.fullName || user.email;
    if (!window.confirm(`Are you sure you want to delete ${displayName}? This action cannot be undone.`)) return;
    try {
      const response = await api.delete(`/admin2/users/${id}.json`);
      if (response.data?.success) {
        showToast('User deleted successfully', 'success');
        setTimeout(() => navigate('/admin2/users'), 1500);
      } else {
        showToast(response.data?.errors?.join(', ') || 'Failed to delete user', 'error');
      }
    } catch (err: any) {
      showToast(err.response?.data?.errors?.join(', ') || 'Failed to delete user', 'error');
    }
  };

  const formatDate = (dateString?: string) => {
    return formatDateTime(dateString);
  };

  if (loading) {
    return (
      <AdminLayout title="Edit user" breadcrumb={[{ label: 'Users', href: '/admin2/users' }]}>
        <LoadingState>Loading user...</LoadingState>
      </AdminLayout>
    );
  }

  if (error || !user) {
    return (
      <AdminLayout title="Edit user" breadcrumb={[{ label: 'Users', href: '/admin2/users' }]}>
        <ErrorState>User not found or you don't have permission to view this user.</ErrorState>
      </AdminLayout>
    );
  }

  const displayName = user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email;

  return (
    <AdminLayout 
      title="Edit user" 
      breadcrumb={[
        { label: 'Users', href: '/admin2/users' },
        { label: `${displayName}${user.email ? ` (${user.email})` : ''}` }
      ]}
    >
      <div style={{ maxWidth: '1100px' }}>
        {/* Tab Navigation */}
        <TabNav>
          <TabLink to={`/admin2/users/${id}/edit`} $active={activeTab === 'info'}>
            User info
          </TabLink>
          <TabLink to={`/admin2/users/${id}/publications`} $active={activeTab === 'publications'}>
            Publications
          </TabLink>
          <TabLink to={`/admin2/users/${id}/stories`} $active={activeTab === 'stories'}>
            Stories
          </TabLink>
          <TabLink to={`/admin2/users/${id}/audit_events`} $active={activeTab === 'audit_events'}>
            Audit Events
          </TabLink>
          <TabLink to={`/admin2/users/${id}/content_origin_permissions`} $active={activeTab === 'content_origin'}>
            Content Origin Access
          </TabLink>
        </TabNav>

        <ContentLayout>
          <MainContent>
            {/* Actions Section */}
            <ActionsSection>
              <SectionTitle style={{ width: '100%', marginBottom: '16px' }}>Actions</SectionTitle>
              {user.username && (
                <ActionLink href={user.portfolioUrl || '#'} target="_blank" rel="noopener noreferrer">
                  👁️ View portfolio
                </ActionLink>
              )}
              {user.policy?.canImpersonate && (
                <Button onClick={handleImpersonate}>🕵️ Impersonate</Button>
              )}
              {user.policy?.canDisable && (
                <Button $variant="warning" onClick={handleDisable}>
                  ⚠️ Disable
                </Button>
              )}
              {user.policy?.canDelete && (
                <Button
                  $variant="danger"
                  onClick={handleDelete}
                  disabled={!user.policy?.eligibleForDestruction}
                  title={
                    !user.policy?.eligibleForDestruction
                      ? 'Users working on active stories cannot be dismissed or deleted until they are removed from those stories or those stories are completed.'
                      : 'Delete this user'
                  }
                >
                  🗑️ Delete
                </Button>
              )}
            </ActionsSection>

            {/* User Info Section */}
            <FormSection>
              <SectionTitle>User info</SectionTitle>
              <FormGrid>
                <FormGroup>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>First name *</Label>
                  <Input
                    type="text"
                    value={formData.firstName || ''}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Last name *</Label>
                  <Input
                    type="text"
                    value={formData.lastName || ''}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Phone number</Label>
                  <Input
                    type="tel"
                    value={formData.phoneNumber || ''}
                    onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Reporting category</Label>
                  <Select
                    value={formData.reportingCategory || ''}
                    onChange={(e) => handleInputChange('reportingCategory', e.target.value)}
                  >
                    {REPORTING_CATEGORIES.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Select>
                </FormGroup>
                <FormGroup>
                  <CheckboxLabel>
                    <Checkbox
                      type="checkbox"
                      checked={formData.textOnlyEmail || false}
                      onChange={(e) => handleInputChange('textOnlyEmail', e.target.checked)}
                    />
                    Only receive email in plain text (i.e. text only)
                  </CheckboxLabel>
                </FormGroup>
                <FormGroup>
                  <CheckboxLabel>
                    <Checkbox
                      type="checkbox"
                      checked={formData.confirmedEmail || false}
                      onChange={(e) => handleInputChange('confirmedEmail', e.target.checked)}
                      disabled={user.confirmed}
                    />
                    Email verified
                  </CheckboxLabel>
                </FormGroup>
              </FormGrid>
            </FormSection>

            {/* Talent Information Section */}
            <FormSection>
              <SectionTitle>Talent information</SectionTitle>
              <FormGrid>
                <FormGroup $fullWidth>
                  <ReadOnlyLabel>Primary skill</ReadOnlyLabel>
                  <ReadOnlyValue>{user.primarySkill || 'Not specified'}</ReadOnlyValue>
                </FormGroup>
                <FormGroup>
                  <Label>Talent status</Label>
                  <Select
                    value={formData.talentStatus || ''}
                    onChange={(e) => handleInputChange('talentStatus', e.target.value)}
                  >
                    {TALENT_STATUSES.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Select>
                </FormGroup>
                <FormGroup>
                  <Label>Talent source</Label>
                  <Select
                    value={formData.talentSource || ''}
                    onChange={(e) => handleInputChange('talentSource', e.target.value)}
                  >
                    {TALENT_SOURCES.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Select>
                </FormGroup>
                <FormGroup $fullWidth>
                  <Label>Expertise</Label>
                  <Select
                    multiple
                    value={formData.expertiseIds || []}
                    onChange={(e) => {
                      const values = Array.from(e.target.selectedOptions, (option) => option.value);
                      handleInputChange('expertiseIds', values);
                    }}
                    style={{ minHeight: '150px' }}
                  >
                    {user.expertise?.map((exp) => (
                      <option key={exp.id} value={exp.id}>
                        {exp.name}
                      </option>
                    ))}
                  </Select>
                  <small style={{ color: '#6b7280', fontSize: '12px' }}>
                    Hold Ctrl/Cmd to select multiple. Full expertise list requires API update.
                  </small>
                </FormGroup>
                <FormGroup>
                  <CheckboxLabel>
                    <Checkbox
                      type="checkbox"
                      checked={formData.talentTrained || false}
                      onChange={(e) => handleInputChange('talentTrained', e.target.checked)}
                    />
                    Talent trained
                  </CheckboxLabel>
                </FormGroup>
                {user.talentTrainedAt && (
                  <FormGroup>
                    <ReadOnlyLabel>Talent trained date</ReadOnlyLabel>
                    <ReadOnlyValue>{formatDate(user.talentTrainedAt)}</ReadOnlyValue>
                  </FormGroup>
                )}
                <FormGroup>
                  <CheckboxLabel>
                    <Checkbox
                      type="checkbox"
                      checked={formData.availableFreelance || false}
                      onChange={(e) => handleInputChange('availableFreelance', e.target.checked)}
                    />
                    Available for freelance
                  </CheckboxLabel>
                </FormGroup>
                <FormGroup $fullWidth>
                  <Label>Internal notes</Label>
                  <Textarea
                    value={formData.internalNotes || ''}
                    onChange={(e) => handleInputChange('internalNotes', e.target.value)}
                    placeholder="Internal notes about this user..."
                  />
                </FormGroup>
              </FormGrid>
            </FormSection>

            {/* Admin Capabilities Section */}
            <FormSection>
              <SectionTitle>Application admin capabilities</SectionTitle>
              <CheckboxGrid>
                <CheckboxLabel>
                  <Checkbox
                    type="checkbox"
                    checked={formData.adminEnabled || false}
                    onChange={(e) => handleInputChange('adminEnabled', e.target.checked)}
                  />
                  Enabled
                </CheckboxLabel>
                <CheckboxLabel>
                  <Checkbox
                    type="checkbox"
                    checked={formData.adminManage || false}
                    onChange={(e) => handleInputChange('adminManage', e.target.checked)}
                  />
                  Manage (make changes)
                </CheckboxLabel>
                <CheckboxLabel>
                  <Checkbox
                    type="checkbox"
                    checked={formData.adminImpersonate || false}
                    onChange={(e) => handleInputChange('adminImpersonate', e.target.checked)}
                  />
                  Impersonate users
                </CheckboxLabel>
                <CheckboxLabel>
                  <Checkbox
                    type="checkbox"
                    checked={formData.adminManageCapabilities || false}
                    onChange={(e) => handleInputChange('adminManageCapabilities', e.target.checked)}
                  />
                  Edit capabilities
                </CheckboxLabel>
                <CheckboxLabel>
                  <Checkbox
                    type="checkbox"
                    checked={formData.adminCreateTransactions || false}
                    onChange={(e) => handleInputChange('adminCreateTransactions', e.target.checked)}
                  />
                  Create transactions
                </CheckboxLabel>
                <CheckboxLabel>
                  <Checkbox
                    type="checkbox"
                    checked={formData.adminManageFeatures || false}
                    onChange={(e) => handleInputChange('adminManageFeatures', e.target.checked)}
                  />
                  Manage features
                </CheckboxLabel>
                <CheckboxLabel>
                  <Checkbox
                    type="checkbox"
                    checked={formData.adminManageUsers || false}
                    onChange={(e) => handleInputChange('adminManageUsers', e.target.checked)}
                  />
                  Manage users (e.g. password, staffing)
                </CheckboxLabel>
                <CheckboxLabel>
                  <Checkbox
                    type="checkbox"
                    checked={formData.adminBulkUploadUsers || false}
                    onChange={(e) => handleInputChange('adminBulkUploadUsers', e.target.checked)}
                  />
                  Bulk upload users
                </CheckboxLabel>
                <CheckboxLabel>
                  <Checkbox
                    type="checkbox"
                    checked={formData.adminManageAdminUser || false}
                    onChange={(e) => handleInputChange('adminManageAdminUser', e.target.checked)}
                  />
                  Disable admin users
                </CheckboxLabel>
              </CheckboxGrid>
              <ButtonGroup>
                <Button onClick={() => navigate('/admin2/users')}>Cancel</Button>
                <Button $variant="primary" onClick={handleSave} disabled={updating}>
                  {updating ? 'Saving...' : 'Save'}
                </Button>
              </ButtonGroup>
            </FormSection>

            {/* Change Password Link */}
            <FormSection>
              <SectionTitle>Change Password</SectionTitle>
              <p style={{ margin: '0 0 16px 0', color: '#6b7280', fontSize: '14px' }}>
                Change the user's password.
              </p>
              <Link 
                to={`/admin2/users/${id}/password`}
                style={{
                  display: 'inline-block',
                  padding: '10px 20px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  borderRadius: '6px',
                  textDecoration: 'none',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                Change Password
              </Link>
            </FormSection>
          </MainContent>

          <Sidebar>
            {/* Residence Information Section */}
            <FormSection>
              <SectionTitle>Residence information</SectionTitle>
              <CurrentResidence>Current residence: {user.fullLocation || 'Not specified'}</CurrentResidence>
              {user.residences && user.residences.length > 0 ? (
                <ResidenceTable>
                  <thead>
                    <tr>
                      <th>Created at</th>
                      <th>City</th>
                      <th>State</th>
                      <th>Country</th>
                    </tr>
                  </thead>
                  <tbody>
                    {user.residences.map((residence) => (
                      <tr key={residence.id}>
                        <td>{formatDate(residence.createdAt)}</td>
                        <td>{residence.city || '-'}</td>
                        <td>{residence.state || '-'}</td>
                        <td>{residence.country || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </ResidenceTable>
              ) : (
                <ReadOnlyValue>No residence history available</ReadOnlyValue>
              )}
            </FormSection>
          </Sidebar>
        </ContentLayout>
      </div>

      {toast && <Toast $type={toast.type}>{toast.message}</Toast>}
    </AdminLayout>
  );
}

export default AdminUserEditPage;
